import React, { useState } from "react";
import { FaRegStar, FaStar } from "react-icons/fa";
import { useReviewRequest } from "../../hooks/useReviewRequest.ts";
import styles from "./ReviewModal.module.css";

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ReviewModal: React.FC<ReviewModalProps> = ({ isOpen, onClose }) => {
  const { loading, error, setError, sendReviewRequest } = useReviewRequest();

  const [author, setAuthor] = useState("");
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const resetForm = () => {
    setAuthor("");
    setText("");
    setRating(5);
    setError("");
  };

  const closeModal = () => {
    onClose();
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!author.trim() || !text.trim()) {
      alert("Заполните имя и текст отзыва");
      return;
    }

    try {
      await sendReviewRequest({
        Author: author.trim(),
        Text: text.trim(),
        Rating: rating,
      });

      setShowSuccess(true);

      window.setTimeout(() => {
        onClose();
        setShowSuccess(false);
        resetForm();
      }, 3000);
    } catch (err) {
      console.error("Ошибка:", err);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.modalBackdrop} onClick={handleBackdropClick}>
      <div className={styles.modalContent}>
        <div className={styles.glow} aria-hidden="true" />

        <div className={styles.header}>
          <div className={styles.headerCopy}>
            <span className={styles.eyebrow}>Обратная связь</span>
            <h2>Оставить отзыв</h2>
            <p className={styles.headerText}>
              Поделитесь впечатлением о работе с нами. Мы внимательно читаем каждую заявку.
            </p>
          </div>

          <button className={styles.closeButton} onClick={closeModal} type="button" aria-label="Закрыть окно">
            ×
          </button>
        </div>

        {showSuccess ? (
          <div className={styles.successBlock}>
            <div className={styles.successIcon}>✓</div>
            <p className={styles.successText}>
              Спасибо за отзыв! Мы обязательно его рассмотрим.
            </p>
            <p className={styles.successHint}>
              Окно закроется через несколько секунд…
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="review-author">
                Ваше имя
              </label>
              <input
                id="review-author"
                type="text"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="Например, Анна"
                className={styles.input}
                required
                maxLength={30}
              />
            </div>

            <div className={styles.field}>
              <div className={styles.ratingHeader}>
                <label className={styles.label}>Оценка</label>
                <span className={styles.ratingValue}>{rating}/5</span>
              </div>

              <div className={styles.ratingStars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={`${styles.starButton} ${rating >= star ? styles.active : ""}`}
                    onClick={() => setRating(star)}
                    aria-label={`Поставить ${star} ${star === 1 ? "звезду" : star < 5 ? "звезды" : "звёзд"}`}
                  >
                    {star <= rating ? <FaStar /> : <FaRegStar />}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.field}>
              <div className={styles.fieldTopLine}>
                <label className={styles.label} htmlFor="review-text">
                  Ваш отзыв
                </label>
                <span className={styles.counter}>{text.length}/500</span>
              </div>

              <textarea
                id="review-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Расскажите, что вам особенно понравилось..."
                rows={5}
                className={styles.textarea}
                required
                maxLength={500}
              />
            </div>

            {error && <p className={styles.error}>{error}</p>}

            <button type="submit" disabled={loading} className={styles.submitButton}>
              {loading ? "Отправляем..." : "Отправить отзыв"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReviewModal;
